import { useCallback, useEffect, useRef, useState } from 'react';

interface UseTypingAnimationConfig {
  typingSpeed?: number; // milliseconds per character
  paragraphDelay?: number; // delay between paragraphs
}

export function useTypingAnimation({
  typingSpeed = 40, // Slow typing for realistic AI transcription feel
  paragraphDelay = 150,
}: UseTypingAnimationConfig = {}) {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Queue of text segments to type
  const textQueueRef = useRef<string[]>([]);
  const currentlyTypingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add new text to the queue and start typing if not already typing
  const addText = useCallback((newText: string) => {
    if (!newText.trim()) {
      console.log('⚠️ Empty or whitespace-only text received, skipping');
      return;
    }
    textQueueRef.current.push(newText);
    // Start typing if not already in progress
    if (!currentlyTypingRef.current) {
      processQueue();
    } else {
      console.log('⏳ Typing already in progress, text queued');
    }
  }, []);

  // Process the text queue with typing animation
  const processQueue = useCallback(() => {
    if (textQueueRef.current.length === 0) {
      currentlyTypingRef.current = false;
      setIsTyping(false);
      return;
    }

    currentlyTypingRef.current = true;
    setIsTyping(true);

    const textToType = textQueueRef.current.shift()!;
    console.log('🎬 Starting to type text:', textToType);
    let currentIndex = 0;

    const typeNextCharacter = () => {
      if (currentIndex < textToType.length) {
        const currentChar = textToType.charAt(currentIndex);
        // Add the current character to the displayed text
        setDisplayedText((prev) => {
          const newText = prev + currentChar;
          if (currentIndex % 20 === 0) {
            // Log every 20 characters to avoid spam
          }
          return newText;
        });
        currentIndex++;

        // Continue typing
        timeoutRef.current = setTimeout(typeNextCharacter, typingSpeed);
      } else {
        // Add paragraph break and delay before next text
        setDisplayedText((prev) => prev + '\n');
        timeoutRef.current = setTimeout(() => {
          processQueue(); // Process next item in queue
        }, paragraphDelay);
      }
    };

    typeNextCharacter();
  }, [typingSpeed, paragraphDelay]);

  // Clear all text and reset
  const clearText = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    textQueueRef.current = [];
    currentlyTypingRef.current = false;
    setDisplayedText('');
    setIsTyping(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    displayedText,
    isTyping,
    addText,
    clearText,
  };
}
